import React, { useEffect, useState } from 'react';
import { getListReactOfParagraph, getParagraphReactionUsers } from '../../../../apis/article.api';
import CommentGreenIcon from '../../../../assets/comment-green-icon.svg';

import CloseIcon from '../../../../assets/close-modal-icon.svg';
import HeartReactionIcon from '../../../../assets/heart-reaction-icon.svg';
import FunnyReactionIcon from '../../../../assets/funny-reaction-icon.svg';
import AngryReactionIcon from '../../../../assets/angry-reaction-icon.svg';
import SadReactionIcon from '../../../../assets/sad-reaction-icon.svg';
import InsightfulReactionIcon from '../../../../assets/insightful-reaction-icon.svg';
import GalaxyBrainReactionIcon from '../../../../assets/galaxy-brain-reaction-icon.svg';
import NeedsCitationIcon from '../../../../assets/needs-citation.svg';
import BadLogicIcon from '../../../../assets/puzzle.svg';

import { formatNumber, handleUserClick } from '../../../../utils/helper.util';
import { ReactListDTO } from '@twygs/shared/publisher-interface';
import Button from 'src/components/Button';
import { useNavigate } from 'react-router-dom';
import VerifiedIcon from '../../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../../assets/founding_creator_icon.png';
import UserAvatar from '../../../../assets/medium_default_optimized.png';

interface ReactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
}

// Primary brand colors
const brandColors = {
  primary: '#4CAF50',
  highlight: '#70BF73',
  auxiliary: 'black'
};

const REACTIONS = [
  { type: 'all', textDisplay: 'All Reactions', key: 'total', color: 'bg-gray-100' },
  { type: 'like', icon: HeartReactionIcon, textDisplay: 'Like', key: 'like', color: 'bg-gray-50' },
  {
    type: 'funny',
    icon: FunnyReactionIcon,
    textDisplay: 'Funny',
    key: 'funny',
    color: 'bg-gray-50'
  },
  {
    type: 'angry',
    icon: AngryReactionIcon,
    textDisplay: 'Angry',
    key: 'angry',
    color: 'bg-gray-50'
  },
  { type: 'sad', icon: SadReactionIcon, textDisplay: 'Sad', key: 'sad', color: 'bg-gray-50' },
  {
    type: 'innovative',
    icon: InsightfulReactionIcon,
    textDisplay: 'Insightful',
    key: 'innovative',
    color: 'bg-gray-50'
  },
  {
    type: 'big_brain',
    icon: GalaxyBrainReactionIcon,
    textDisplay: 'Big Brain',
    key: 'big_brain',
    color: 'bg-gray-50'
  },
  {
    type: 'need_source',
    icon: NeedsCitationIcon,
    textDisplay: 'Needs Source',
    key: 'need_source',
    color: 'bg-gray-50'
  },
  {
    type: 'poor_logic',
    icon: BadLogicIcon,
    textDisplay: 'Poor Logic',
    key: 'poor_logic',
    color: 'bg-gray-50'
  }
];

const ReactionStatsModal: React.FC<ReactionModalProps> = ({ isOpen, onClose, nodeId }) => {
  const [reactData, setReactData] = useState<ReactListDTO | null>(null);
  const [reactionUsers, setReactionUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedTab, setSelectedTab] = useState('summary');
  const [selectedReactionFilter, setSelectedReactionFilter] = useState<string>('all');
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    hasNextPage: false
  });
  
  const navigate = useNavigate();

  const fetchReactionUsers = async (reactionType?: string, page: number = 1) => {
    try {
      setIsLoadingUsers(true);
      const response = await getParagraphReactionUsers(nodeId, {
        reactionType: reactionType === 'all' ? undefined : reactionType,
        page,
        limit: 20
      });
      
      if (response.data) {
        if (page === 1) {
          setReactionUsers(response.data);
        } else {
          setReactionUsers(prev => [...prev, ...response.data]);
        }
        setUsersPagination(response.pagination);
      }
    } catch (err) {
      console.error('Failed to load reaction users:', err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (isOpen && nodeId) {
      const fetchData = async () => {
        const data = await getListReactOfParagraph(nodeId);
        if (isMounted) {
          setReactData(data);
          // Fetch users for the initial view
          await fetchReactionUsers('all', 1);
        }
      };

      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen, nodeId]);

  useEffect(() => {
    if (isOpen && nodeId && selectedReactionFilter) {
      fetchReactionUsers(selectedReactionFilter, 1);
    }
  }, [selectedReactionFilter]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg w-full max-w-[673px] max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex justify-between items-center p-4 md:p-6 border-b border-[#D1C8C4] flex-shrink-0">
          <h3 className="text-lg md:text-xl font-[800] text-gray-900">Reactions and Comments</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full flex-shrink-0">
            <img src={CloseIcon} alt="Close" className="w-6 h-6 md:w-8 md:h-8" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">

          <p className="text-sm md:text-base">
            Review a breakdown of all interactions made on this{' '}
            <span className="font-bold">section</span>:
          </p>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-4 mt-4">
            <button
              className={`px-4 py-2 text-sm md:text-base ${
                selectedTab === 'summary'
                  ? 'text-growth-green-600 border-b-2 border-growth-green-500 font-[800]'
                  : 'text-gray-500 hover:text-gray-700 font-[600]'
              }`}
              onClick={() => setSelectedTab('summary')}
            >
              Summary
            </button>
            <button
              className={`px-4 py-2 text-sm md:text-base ${
                selectedTab === 'users'
                  ? 'text-growth-green-600 border-b-2 border-growth-green-500 font-[800]'
                  : 'text-gray-500 hover:text-gray-700 font-[600]'
              }`}
              onClick={() => setSelectedTab('users')}
            >
              Users
            </button>
          </div>

          {selectedTab === 'summary' ? (
            <>
              <p className="text-sm md:text-base mb-4 font-bold">Section's Reaction Summary</p>
              
              {/* Summary Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-green-50 p-3 md:p-4 rounded-lg mb-6">
            <div className="text-left bg-[white] rounded-lg border-[1px] border-[#F2F3F7] p-4 md:p-6 overflow-hidden">
              <div className="text-gray-800 text-xs md:text-sm font-bold">Total Interactions:</div>
              <div className="text-lg md:text-xl font-bold">
                {formatNumber(
                  reactData
                    ? Object.values(reactData).reduce(
                        (sum: number, value: number) => sum + value,
                        0
                      ) - reactData?.total
                    : 0
                )}
              </div>
            </div>
            <div className="text-left bg-[white] rounded-lg border-[1px] border-[#F2F3F7] p-4 md:p-6 overflow-hidden">
              <div className="text-gray-800 text-xs md:text-sm font-bold">Total Comments:</div>
              <div className="flex flex-col md:flex-row items-center gap-1">
                <img src={CommentGreenIcon} alt="Comments" className="w-5 h-5 md:w-6 md:h-6" />
                <p className="text-lg md:text-xl inline font-bold">{formatNumber(reactData?.comment || 0)}</p>
                <p className="text-xs md:text-sm inline mt-[1px]">Comments</p>
              </div>
            </div>
            <div className="text-left bg-[white] rounded-lg border-[1px] border-[#F2F3F7] p-4 md:p-6 overflow-hidden">
              <div className="text-gray-800 text-xs md:text-sm font-bold">Top Reactions:</div>
              <div className="flex flex-col gap-2 md:gap-1">
                {REACTIONS.filter((r) => r.type !== 'all')
                  .map((r) => ({ ...r, count: reactData?.[r.key] || 0 }))
                  .filter((r) => r.count > 0) // Changed from >= 0 to > 0
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 2)
                  .map((r) => (
                    <div key={r.key} className="flex items-center gap-1 text-xs md:text-sm">
                      <img src={r.icon} alt={r.textDisplay} className="w-4 h-4" />
                      <p className="text-sm md:text-lg inline font-bold">{r.count}</p>
                      <span className="text-xs md:text-sm">{`${r.textDisplay}${r.count > 1 ? 's' : ''}`}</span>
                    </div>
                  ))}
                {/* Show "No reactions yet" if no reactions */}
                {REACTIONS.filter((r) => r.type !== 'all')
                  .map((r) => ({ ...r, count: reactData?.[r.key] || 0 }))
                  .filter((r) => r.count > 0).length === 0 && (
                  <div className="text-xs md:text-sm text-gray-500 italic">
                    No reactions yet
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Distribution Section */}
          <h4 className="font-bold text-sm md:text-base mb-2">Section's Reaction Distribution</h4>
          <div className="space-y-2 md:space-y-3 border-[1px] border-[#EAECF0] p-3 md:p-4 rounded-lg">
            {REACTIONS.filter((r) => r.type !== 'all').map((reaction) => {
              const count = reactData?.[reaction.key] || 0;
              const totalReactions = REACTIONS.reduce(
                (sum, r) => (r.key !== 'total' ? sum + (reactData?.[r.key] || 0) : sum),
                0
              );
              const percentage = totalReactions > 0 ? Math.round((count / totalReactions) * 100) : 0;

              return (
                <div key={reaction.type} className="flex items-center justify-between gap-2">
                  <div className="flex items-center text-sm md:text-base gap-2 min-w-[100px] md:w-[120px]">
                    <img src={reaction.icon} alt={reaction.textDisplay} className="w-5 h-5 md:w-6 md:h-6" />
                    <span className="truncate">{reaction.textDisplay}</span>
                  </div>
                  <div className="flex-1 mx-2 md:mx-4 bg-gray-200 rounded-full h-3 md:h-4">
                    <div
                      className="h-3 md:h-4 bg-growth-green-500 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="text-xs md:text-sm text-gray-700 min-w-[60px] text-right whitespace-nowrap">
                    {count} ({percentage}%)
                  </div>
                </div>
              );
            })}
          </div>
            </>
          ) : selectedTab === 'users' ? (
            /* Users view */
            <div>
              <h4 className="text-sm md:text-base font-bold mb-3">Users Who Reacted</h4>

              {/* Filter by reaction type */}
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Filter by reaction:</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      selectedReactionFilter === 'all'
                        ? 'bg-growth-green-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => setSelectedReactionFilter('all')}
                  >
                    All ({formatNumber((reactData?.total || 0) - (reactData?.comment || 0))})
                  </button>
                  {REACTIONS.slice(1).filter(r => (reactData?.[r.key] || 0) > 0).map((reaction) => (
                    <button
                      key={reaction.key}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                        selectedReactionFilter === reaction.key
                          ? 'bg-growth-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => setSelectedReactionFilter(reaction.key)}
                    >
                      <img src={reaction.icon} alt={reaction.textDisplay} className="w-4 h-4" />
                      {formatNumber(reactData?.[reaction.key] || 0)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Users list */}
              <div className="max-h-96 overflow-y-auto">
                {isLoadingUsers && reactionUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-growth-green-500 border-t-transparent"></div>
                    <p className="mt-2 text-gray-600">Loading users...</p>
                  </div>
                ) : reactionUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <p className="text-gray-500">No users found for this reaction</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reactionUsers.map((userReaction, index) => (
                      <div
                        key={`${userReaction.user.id}-${index}`}
                        className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={(e) => handleUserClick(e, () => navigate(`/${userReaction.user.username}`))}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={userReaction.user.imgUrl || UserAvatar}
                            alt={userReaction.user.firstName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">
                                {userReaction.user.firstName}
                              </span>
                              {userReaction.user.isVerifiedAuthor && (
                                <img src={VerifiedIcon} alt="verified" className="w-3.5 h-3.5" />
                              )}
                              {userReaction.user.isFoundingCreator && (
                                <img src={FoundingCreatorIcon} alt="Founding Creator" title="Founding Creator" className="w-3.5 h-3.5" />
                              )}
                              {userReaction.user.username && (
                                <span className="text-xs text-gray-500 truncate">
                                  @{userReaction.user.username}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <img 
                              src={REACTIONS.find(r => r.key === userReaction.reactionType)?.icon} 
                              alt={userReaction.reactionTypeDisplay}
                              className="w-5 h-5"
                            />
                            <span className="text-xs bg-growth-green-100 text-growth-green-800 px-2 py-1 rounded-full font-medium">
                              {userReaction.reactionTypeDisplay}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Load more button */}
                    {usersPagination.hasNextPage && (
                      <button
                        className="w-full py-2 px-4 border border-growth-green-500 text-growth-green-600 rounded-lg hover:bg-growth-green-50 transition-colors font-medium"
                        onClick={() => fetchReactionUsers(selectedReactionFilter, usersPagination.page + 1)}
                        disabled={isLoadingUsers}
                      >
                        {isLoadingUsers ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-growth-green-500 border-t-transparent"></div>
                            Loading more...
                          </div>
                        ) : (
                          `Load More (${usersPagination.totalPages - usersPagination.page} pages remaining)`
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ReactionStatsModal;
